import { useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";

/** Product policy: max idle time before auto-logout (no click, keydown, focus, or scroll). */
export const DASHBOARD_IDLE_TIMEOUT_MINUTES = 120;

const TIMEOUT_MS = DASHBOARD_IDLE_TIMEOUT_MINUTES * 60 * 1000;

/** Coalesce burst events (typing, scroll) without skipping idle extension (throttle-only bug). */
const ACTIVITY_DEBOUNCE_MS = 300;

export const useDashboardSessionTimeout = () => {
  const { token, logout } = useAuth();
  const timeoutRef = useRef<number | null>(null);
  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    if (!token) {
      return;
    }

    const scheduleIdleLogout = () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = window.setTimeout(() => {
        logout();
        window.location.href = "/login";
      }, TIMEOUT_MS);
    };

    const bumpActivity = () => {
      if (debounceRef.current !== null) {
        window.clearTimeout(debounceRef.current);
      }
      debounceRef.current = window.setTimeout(() => {
        debounceRef.current = null;
        scheduleIdleLogout();
      }, ACTIVITY_DEBOUNCE_MS);
    };

    const activityEvents: (keyof WindowEventMap)[] = [
      "click",
      "mousedown",
      "pointerdown",
      "touchstart",
      "touchmove",
      "keydown",
      "keypress",
      "focus",
      "scroll",
    ];

    activityEvents.forEach((event) => {
      window.addEventListener(event, bumpActivity, { passive: true });
    });

    bumpActivity();

    return () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
      }
      if (debounceRef.current !== null) {
        window.clearTimeout(debounceRef.current);
      }
      activityEvents.forEach((event) => {
        window.removeEventListener(event, bumpActivity);
      });
    };
  }, [token, logout]);
};

export default useDashboardSessionTimeout;

